import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/min";

/** Best-known ISO-2 country for each calling code. Calling codes are
 *  not unique (``+1`` is US + Canada + Caribbean, ``+44`` is GB + IM),
 *  so we hand-pick the "principal" country for the shared codes the
 *  staff app cares about and fall through to whatever ``getCountries``
 *  iterates first for everything else. */
const PRIORITY_ISO_FOR_CODE: Record<string, CountryCode> = {
  "1": "US",
  "7": "RU",
  "44": "GB",
  "39": "IT",
};

const CODE_TO_ISO = (() => {
  const map = new Map<string, CountryCode>();
  for (const iso of getCountries().toSorted()) {
    const code = getCountryCallingCode(iso);
    const priority = PRIORITY_ISO_FOR_CODE[code];
    if (priority) {
      map.set(code, priority);
    } else if (!map.has(code)) {
      map.set(code, iso);
    }
  }
  return map;
})();

/** ISO-2 short name for a calling code (e.g. ``"+57"`` → ``"CO"``).
 *  Returns ``""`` when the code isn't recognised, so the caller can
 *  decide how to render the unknown state. */
export function isoForDialCode(code: string): string {
  if (!code.startsWith("+")) return "";
  return CODE_TO_ISO.get(code.slice(1)) ?? "";
}

const KNOWN_DIAL_CODES = [...CODE_TO_ISO.keys()].toSorted((a, b) => b.length - a.length);

/** Split an E.164-ish phone into ``{code, national}``. Matches the
 *  longest known calling-code prefix so e.g. ``+593...`` (Ecuador)
 *  doesn't get split as ``+5`` + ``93...``. Default code when nothing
 *  is set: ``+57`` (Colombia) — the staff app is operated from there. */
export function splitDialCode(
  value: string,
  defaultCode = "+57",
): { code: string; national: string } {
  const trimmed = value.trim();
  if (!trimmed) return { code: defaultCode, national: "" };
  if (!trimmed.startsWith("+")) {
    return { code: defaultCode, national: trimmed.replace(/\D/g, "") };
  }
  const digits = trimmed.slice(1).replace(/\D/g, "");
  for (const code of KNOWN_DIAL_CODES) {
    if (digits.startsWith(code)) {
      return { code: `+${code}`, national: digits.slice(code.length) };
    }
  }
  // Unrecognised: keep whatever the operator typed up to 4 digits as
  // the code so the input stays under their control, the rest is
  // treated as the national portion.
  const m = digits.match(/^(\d{1,4})(\d*)$/);
  if (m) return { code: `+${m[1]}`, national: m[2] };
  return { code: defaultCode, national: "" };
}

/** Mirror of ``Guest.normalize_phone`` on the backend: strip non-digits,
 * keep a leading ``+``. Used to compare an input against the duplicate-
 * lookup response (which is also normalized server-side). */
export function normalizePhoneForCompare(phone: string): string {
  const trimmed = phone.trim();
  if (trimmed.startsWith("+")) {
    return "+" + trimmed.slice(1).replace(/\D/g, "");
  }
  return trimmed.replace(/\D/g, "");
}

/** ``react-international-phone`` keeps the country dial code in the
 * input even when the user hasn't typed any national digits, so an
 * untouched field reads back as e.g. ``"+57"``. National numbers always
 * have at least 4 digits past the prefix, so anything with ≤3 total
 * digits is treated as "no phone provided" before submit — otherwise the
 * backend stores the raw dial code as a Guest identifier and concurrent
 * empty submissions collide on the partial unique index on phone. */
export function trimDialCodeOnly(phone: string): string {
  const trimmed = phone.trim();
  if (!trimmed.startsWith("+")) return trimmed;
  const digits = trimmed.slice(1).replace(/\D/g, "");
  return digits.length <= 3 ? "" : trimmed;
}

/** Full libphonenumber validation. ``react-international-phone`` only
 * formats — it doesn't tell us whether the digits past the dial code
 * actually form a real number in that country's plan. Pair it with
 * ``parsePhoneNumberFromString`` so the staff app rejects garbage before
 * it hits the backend (where ``django-phonenumber-field`` would 422 with
 * a less friendly error). Returns ``true`` for empty input — emptiness
 * is "no phone provided" and is handled at submit-time per form. */
export function isValidPhone(phone: string): boolean {
  const cleaned = trimDialCodeOnly(phone);
  if (!cleaned) return true;
  const parsed = parsePhoneNumberFromString(cleaned);
  return parsed?.isValid() ?? false;
}
